import type { IAdViewCarLeasingDetails } from "@finn-no/adview-provider-frontend-types";
import { Trans, useLingui } from "@lingui/react";
import { formatDate, formatNumber, formatPrice } from "@mobility-item-page/utilities";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import FieldValuePair from "./FieldValuePair.tsx";

type Props = {
  leasingDetails?: IAdViewCarLeasingDetails;
};

const LeasingInfo = ({ leasingDetails }: Props) => {
  const { i18n } = useLingui();
  const { locale, brand } = useAppConfig();
  const formatedTerminationDate = formatDate(leasingDetails?.termination_date, locale);
  const formatedMileage = formatNumber(leasingDetails?.mileage_remaining, locale);

  if (!leasingDetails) {
    return null;
  }

  return (
    <section className="border-t mt-40 pt-40">
      <h2 className="t3 mb-0">
        <Trans
          id="itemPageLayout.leasingInfo.title"
          message="Leasing agreement"
          comment="Title for leasing agreement section"
        />
      </h2>
      <dl className="columns-2 md:columns-3 break-words mb-0 mt-16">
        {formatedMileage ? (
          <FieldValuePair
            field={i18n.t({
              id: "itemPageLayout.leasingInfo.remainingMilage.text",
              message: "Remaining kilometers in the agreement",
            })}
            value={i18n.t({
              id: "itemPageLayout.common.distance.kilometer.postfix",
              message: "{distance} km",
              values: { distance: formatedMileage },
            })}
          />
        ) : null}

        {leasingDetails?.remaining_month != null ? (
          <FieldValuePair
            field={i18n.t({
              id: "itemPageLayout.leasingInfo.remainingMonth.text",
              message: "Number of terms until the agreement expires",
            })}
            value={leasingDetails.remaining_month}
          />
        ) : null}

        {formatedTerminationDate ? (
          <FieldValuePair
            field={i18n.t({ id: "itemPageLayout.leasingInfo.terminationDate.text", message: "Lease termination date" })}
            value={formatedTerminationDate}
            suppressHydrationWarning
          />
        ) : null}

        {leasingDetails?.change_renter_fee != null ? (
          <FieldValuePair
            field={i18n.t({
              id: "itemPageLayout.leasingInfo.changeRenterFee.text",
              message: "Fee for change of debtor",
            })}
            value={formatPrice(leasingDetails.change_renter_fee, locale, brand)}
          />
        ) : null}

        {leasingDetails?.company ? (
          <FieldValuePair
            field={i18n.t({ id: "itemPageLayout.leasingInfo.company.text", message: "Leasing company" })}
            value={leasingDetails.company}
          />
        ) : null}

        {leasingDetails?.return_location ? (
          <FieldValuePair
            field={i18n.t({
              id: "itemPageLayout.leasingInfo.returnLocation.text",
              message: "Return location for the vehicle",
            })}
            value={leasingDetails.return_location}
          />
        ) : null}
      </dl>
    </section>
  );
};

export default LeasingInfo;
